// =============================================
// קוד שיוך (QR) לטאבלט: מפתחות החיבור לשערי הנוכחות.
// הפורמט חייב להתאים ל-parsePairingQr ב-BLE/Engine/src/pairingQr.ts (שמפענח בטאבלט):
//   {"t":"kipikid-pairing","v":1,"peerKey":"<base64>","gateKey":"<מפתח>"}  (לפחות מפתח אחד)
// הקוד מכיל סודות: לא נשמר בשרת ולא בדפדפן, ומוצג רק עד סגירת החלון.
// =============================================

export const PAIRING_QR_TYPE = 'kipikid-pairing';
export const PAIRING_QR_VERSION = 1;
export const MIN_PEER_KEY_BYTES = 16;
export const MIN_GATE_KEY_CHARS = 16;

const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;

/** אורך בבתים של מחרוזת base64 תקינה, או -1 אם אינה תקינה */
function base64ByteLength(value: string): number {
  if (!BASE64_PATTERN.test(value) || value.length % 4 === 1) return -1;
  const padding = (value.match(/=+$/)?.[0].length ?? 0);
  return Math.floor(((value.length - padding) * 6) / 8);
}

export function isValidPeerKey(value: string): boolean {
  return base64ByteLength(value.trim()) >= MIN_PEER_KEY_BYTES;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** base64 ללא תלות ב-btoa (שמתנהג שונה בסביבות שונות) */
function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const c = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += B64[a >> 2] + B64[((a & 3) << 4) | (b >> 4)];
    out += i + 1 < bytes.length ? B64[((b & 15) << 2) | (c >> 6)] : '=';
    out += i + 2 < bytes.length ? B64[c & 63] : '=';
  }
  return out;
}

/** מפתח חתימה אקראי חדש (32 בתים, base64), מ-crypto מאובטח של הדפדפן */
export function generatePeerKey(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return bytesToBase64(bytes);
}

export type PairingKeys = { peerKey?: string; gateKey?: string };

/** בונה את טקסט ה-QR. זורק שגיאה על מפתח לא תקין או כשאין אף מפתח, כדי שלא ייווצר קוד שהטאבלט ידחה */
export function buildPairingPayload({ peerKey, gateKey }: PairingKeys): string {
  const peer = peerKey?.trim();
  const gate = gateKey?.trim();
  if (!peer && !gate) throw new Error('נדרש לפחות מפתח אחד');
  if (peer && !isValidPeerKey(peer)) throw new Error('מפתח החתימה לא תקין (base64, לפחות 16 בתים)');
  if (gate && gate.length < MIN_GATE_KEY_CHARS) throw new Error('מפתח המכשיר קצר מדי');

  return JSON.stringify({
    t: PAIRING_QR_TYPE,
    v: PAIRING_QR_VERSION,
    ...(peer ? { peerKey: peer } : {}),
    ...(gate ? { gateKey: gate } : {}),
  });
}
